import { useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { COMPLETION_REQ_UNIT_COST, COMPLETION_RES_UNIT_COST } from './constants';
import type { TokenData, DailyTokenUsage } from './types';

interface UserTokenResult {
    user: string | null;
    tokens: number;
}

/**
 * Find the user with the most token usage
 * @param data - Array of token data arrays
 * @param application - Optional application filter
 * @returns Object containing the user email and their token count
 */
export const findUserWithMostTokens = (data: TokenData[][], application?: string): UserTokenResult => {
    const tokenUsage: Record<string, number> = {};

    data.forEach((sublist) => {
        sublist
            .filter((item) => item.api_type === 'Completion')
            .filter((item) => {
                if (!application || application === 'All') {
                    return true;
                } else {
                    return item.project_type === application;
                }
            })
            .forEach((record) => {
                const userEmail = record.user_email;
                const totalTokens = record.request_token_count + record.response_token_count;
                if (tokenUsage[userEmail]) {
                    tokenUsage[userEmail] += totalTokens;
                } else {
                    tokenUsage[userEmail] = totalTokens;
                }
            });
    });

    // Find the user with the most tokens
    let maxTokens = 0;
    let userWithMostTokens: string | null = null;

    for (const userEmail in tokenUsage) {
        if (tokenUsage[userEmail] > maxTokens) {
            maxTokens = tokenUsage[userEmail];
            userWithMostTokens = userEmail;
        }
    }

    return { user: userWithMostTokens, tokens: maxTokens };
};

/**
 * Calculate daily token usage or cost
 * @param data - Array of token data
 * @param type - Type of calculation: 'Tokens', 'EmbeddingCost', or 'CompletionCost'
 * @returns Array of daily usage data
 */
export const calculateTokenUsageDaily = (
    data: TokenData[],
    type?: 'Tokens' | 'EmbeddingCost' | 'CompletionCost'
): DailyTokenUsage[] => {
    const tokenUsageEachDay: Record<string, number> = {};

    data.forEach((item) => {
        const createTime = dayjs(item.created_at, 'YYYY-MM-DD');
        const date = createTime.format('YYYY-MM-DD');
        let tokenUsage = 0;

        switch (type) {
            case 'Tokens':
                tokenUsage = item.request_token_count + item.response_token_count;
                break;
            case 'EmbeddingCost':
                // Note: EMBEDDING_UNIT_COST is not defined in the original, keeping for reference
                tokenUsage = (item.request_token_count + item.response_token_count) * 0;
                break;
            case 'CompletionCost':
                tokenUsage =
                    item.request_token_count * COMPLETION_REQ_UNIT_COST(item.model_name || '') +
                    item.response_token_count * COMPLETION_RES_UNIT_COST(item.model_name || '');
                break;
            default:
                tokenUsage = item.request_token_count + item.response_token_count;
        }

        if (tokenUsageEachDay[date]) {
            tokenUsageEachDay[date] += tokenUsage;
        } else {
            tokenUsageEachDay[date] = tokenUsage;
        }
    });

    const result = Object.entries(tokenUsageEachDay).map(([date, tokenUsage]) => ({
        Date: date,
        tokenUsageEachDay: tokenUsage,
    }));

    return result;
};

/**
 * Custom hook to get the previous value of a state/prop
 * Useful for comparing previous and current values
 * @param value - The value to track
 * @returns The previous value
 */
export function usePrevious<T>(value: T): T | undefined {
    const ref = useRef<T>();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}
